import { useCallback } from 'react';
import type { DocumentDesign } from '@blogforge/shared';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEditorStore } from '../../stores/editorStore.js';
import { BlockWrapper } from '../BlockWrapper.js';
import { BlockAdder } from '../BlockAdder.js';
import { documentDesignVars, isSameDragParent, pathDepth } from '../../helpers.js';
import { useAvailableTemplateTokens } from '../../hooks/useAvailableTemplateTokens.js';
import { DocumentKindContext } from './DocumentKindContext.js';
import type { DragData } from '../../types.js';

export interface EditorCanvasProps {
  /**
   * Document kind rendered in this canvas. When set, the block adder also
   * offers reusable components whose declared target includes this kind. It is
   * also published as `data-doc-kind`, because the two media style the same
   * block differently: a post's headings take `fontHeading`, an email's inherit
   * the body font (its renderer never applies the heading token to content).
   */
  contentKind?: 'post' | 'email';
  rawVariableDefinitions: Record<string, unknown> | undefined;
  /**
   * The design the build will use (`useDocumentDesign`): the resolved token
   * cascade plus the content column width. Published on the canvas as `--doc-*`
   * variables, which the canvas CSS consumes, so blocks preview the document's
   * own type, colour and metrics rather than the editor's.
   *
   * What this deliberately does NOT change: editor affordances — drop-zone
   * borders, focus rings, selection outlines, drag handles and the minimum gap
   * between blocks — stay on the editor's palette, because they must stay
   * legible on any document colour and appear nowhere in the build. Exact
   * fidelity is what Preview is for.
   *
   * Omit it (component designer, still-loading document) to keep the editor's
   * own colours and metrics; unusable token values are dropped individually.
   */
  design?: DocumentDesign | undefined;
}

/**
 * The drag-and-drop block canvas shared by the post, email and component
 * editors: sensors, reordering within the same parent, sortable block
 * wrappers and the trailing "add block" affordance. Reads blocks + move
 * actions straight from the editor store, so it is content-kind agnostic.
 */
export function EditorCanvas({ contentKind, rawVariableDefinitions, design }: EditorCanvasProps) {
  const blocks = useEditorStore((s) => s.blocks);
  const moveBlock = useEditorStore((s) => s.moveBlock);
  const moveBlockInPath = useEditorStore((s) => s.moveBlockInPath);

  // The document's whole design is published once, as custom properties on the
  // canvas area, and consumed by the canvas CSS — custom properties inherit, so
  // one declaration reaches the paper, the blocks and anything nested inside
  // them, and the stylesheet keeps owning layout, borders and the responsive
  // rules. See documentDesignVars for the per-token validity checks.
  const canvasStyle = documentDesignVars(design);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Audience attributes — the workspace's cached merge fields — are offered
   * only in an email. A post or a component is published as static HTML, so an
   * attribute token there would be built literally into the page; the email
   * resolves it per recipient. Computed here, where the document kind is known,
   * and handed down as plain data so every block shares one list and one
   * request.
   */
  const { attributeKeys } = useAvailableTemplateTokens([], {
    includeAudienceAttributes: contentKind === 'email',
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeData = active.data.current as DragData;
      const overData = over.data.current as DragData;

      // Guard: only reorder within the same parent
      if (!isSameDragParent(activeData, overData)) return;

      const depth = pathDepth(activeData);

      if (depth === 0) {
        // Root-level reorder
        moveBlock(String(active.id), String(over.id));
      } else {
        moveBlockInPath(String(active.id), String(over.id), activeData.path);
      }
    },
    [moveBlock, moveBlockInPath],
  );

  return (
    <div className="canvas-area canvas-scroll flex-1" style={canvasStyle}>
      <DocumentKindContext.Provider value={contentKind}>
        <div className="canvas-paper" data-doc-kind={contentKind ?? 'component'}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((block) => (
                <BlockWrapper
                  key={block.id}
                  block={block}
                  path={[]}
                  rawVariableDefinitions={rawVariableDefinitions}
                  audienceAttributeKeys={attributeKeys}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Add block button */}
          <BlockAdder contentKind={contentKind} />
        </div>
      </DocumentKindContext.Provider>
    </div>
  );
}
